/**
 * The chart palette.
 *
 * Every value here was produced by enumerating candidate hue orderings and
 * keeping only those that clear the colourblind-separation, lightness-band,
 * chroma and normal-vision gates in BOTH light and dark mode. Do not adjust a
 * hex by eye — re-run the enumeration and pick from the passing set, or the
 * palette silently stops being readable for colourblind viewers.
 *
 * Recorded results for the shipped order (adjacent pairlist — stacked bars,
 * grouped bars, lines, pies, which is every form this product renders):
 *
 *   light  worst adjacent CVD ΔE 9.1 (protan), normal-vision ΔE 19.6
 *   dark   worst adjacent CVD ΔE 8.4 (protan), normal-vision ΔE 19.3
 *
 * Targets are CVD ΔE >= 8 and normal-vision ΔE >= 15 (OKLab ×100), so both
 * modes clear with margin. 90 orderings passed; this one was chosen among the
 * eight jointly-best for its opening colours, since most charts show two or
 * three series and those are the ones people actually see.
 *
 * Not used for scatter/bubble-style forms, which compare every pair rather
 * than just neighbours and would cap this palette at two slots. That is fine:
 * survey answers are categorical, so there is nothing numeric to plot on two
 * axes, and no such chart exists in the product.
 *
 * Slot 1 is a green chosen to sit in the brand's family. The brand primary
 * itself (#35573a) cannot be a series colour — at OKLCH L 0.42 / chroma 0.06
 * it fails both the lightness band and the chroma floor, and reads as grey
 * next to real hues. It stays what it is: a UI colour.
 */
/** Chart surfaces the palette was validated against. */
export const CHART_SURFACE = {
    light: '#ffffff',
    dark: '#14181b',
};
/**
 * Categorical hues, in fixed order. Assign by slot and never cycle: a ninth
 * series is not a generated colour, it folds into "Other".
 */
export const CHART_PALETTE = {
    light: [
        '#1f8a4d', // green   — brand family
        '#2a78d6', // blue
        '#eb6834', // orange
        '#1baf7a', // aqua
        '#eda100', // yellow
        '#e87ba4', // magenta
        '#4a3aa7', // violet
        '#e34948', // red
    ],
    dark: [
        '#2f9d5c',
        '#3987e5',
        '#d95926',
        '#199e70',
        '#c98500',
        '#d55181',
        '#9085e9',
        '#e66767',
    ],
};
/**
 * Slots whose light-mode fill sits below 3:1 against a white card. They are
 * legal, but only alongside a visible label or the table view — never as the
 * sole carrier of meaning. The renderer always draws a legend, so this is
 * satisfied; the list is here so it stays a deliberate choice.
 */
export const LOW_CONTRAST_LIGHT_SLOTS = [3, 4, 5];
/** Reserved for state, never reused as "series 9". Always shipped with a label. */
export const SEMANTIC_CHART_COLORS = {
    positive: '#249689', // semantic.success
    negative: '#ff5963', // semantic.error
    caution: '#f9cf58', // semantic.warning
    neutral: '#95a1ac', // custom.grayIcon
};
/** Ink for a suppressed or empty cell — deliberately not a series colour. */
export const CHART_MUTED = {
    light: '#95a1ac',
    dark: '#57636c',
};
/**
 * Everything on a chart that is not a series: axes, gridlines, the tooltip,
 * the interval whiskers.
 *
 * These were module-level literals inside `chart.tsx`, and all of them were
 * light-mode values — including a tooltip that hardcoded the light surface
 * and near-black text. The palette has had a validated dark ramp all along
 * and nothing ever asked for it, so a chart in dark mode drew light-mode
 * series colours on a dark card.
 *
 * Kept here beside the palette rather than in the component because it is the
 * same decision: what a chart is allowed to be coloured. A component that
 * wants to theme a chart asks for a mode, it does not invent a colour.
 */
export const CHART_CHROME = {
    light: {
        axis: 'rgba(128,128,128,0.4)',
        grid: 'rgba(128,128,128,0.15)',
        /** The fill behind a hovered category, so the cursor is felt not read. */
        cursor: 'rgba(128,128,128,0.15)',
        tooltipSurface: CHART_SURFACE.light,
        tooltipBorder: 'rgba(17, 24, 39, 0.1)',
        tooltipShadow: '0 4px 14px rgba(17, 24, 39, 0.15)',
        tooltipText: '#111827',
        /** Drawn over a bar, so it contrasts with the series fill, not the card. */
        interval: 'rgba(0,0,0,0.45)',
        legendText: '#4b5563',
    },
    dark: {
        axis: 'rgba(160,160,160,0.45)',
        grid: 'rgba(160,160,160,0.18)',
        cursor: 'rgba(160,160,160,0.18)',
        tooltipSurface: CHART_SURFACE.dark,
        tooltipBorder: 'rgba(255, 255, 255, 0.14)',
        tooltipShadow: '0 4px 14px rgba(0, 0, 0, 0.5)',
        tooltipText: '#f3f4f6',
        interval: 'rgba(255,255,255,0.55)',
        legendText: '#d1d5db',
    },
};
export const CHART_PALETTE_SIZE = CHART_PALETTE.light.length;
/** The label used when a dimension's tail is rolled up. */
export const OTHER_BUCKET_LABEL = 'Other';
/** The label used for respondents who skipped a question. */
export const NO_ANSWER_LABEL = '(no answer)';
/** Colour for slot `index`, by position. Wraps only as a last resort. */
export const getSeriesColor = (index, mode = 'light') => CHART_PALETTE[mode][((index % CHART_PALETTE_SIZE) + CHART_PALETTE_SIZE) % CHART_PALETTE_SIZE];
// Stable, order-independent hash. Two charts showing "Engineering" give it the
// same colour, and adding a category does not repaint the others.
const hashLabel = (label) => {
    let hash = 0;
    for (let i = 0; i < label.length; i += 1) {
        hash = (hash << 5) - hash + label.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
};
/**
 * Colour for a named series. Prefer this over {@link getSeriesColor} wherever
 * the set of categories can change — filtering a department out must not
 * recolour the ones that remain, and the same answer should look the same on
 * every tile of a dashboard.
 *
 * "Other" and "(no answer)" are not identities, so they always take the muted
 * ink rather than competing with real categories for a hue.
 */
export const getSeriesColorForLabel = (label, mode = 'light') => {
    const normalized = (label ?? '').trim().toLowerCase();
    if (normalized === OTHER_BUCKET_LABEL.toLowerCase() ||
        normalized === NO_ANSWER_LABEL.toLowerCase()) {
        return CHART_MUTED[mode];
    }
    return getSeriesColor(hashLabel(normalized), mode);
};
/**
 * Colours for a whole series list at once. Collisions are pushed to the next
 * free slot, so two categories on the same chart never share a colour even
 * when their hashes land together.
 */
export const getSeriesColors = (labels, mode = 'light') => {
    const used = new Set();
    return (labels ?? []).map((label) => {
        const preferred = getSeriesColorForLabel(label, mode);
        if (preferred === CHART_MUTED[mode] || !used.has(preferred)) {
            used.add(preferred);
            return preferred;
        }
        const start = CHART_PALETTE[mode].indexOf(preferred);
        for (let step = 1; step < CHART_PALETTE_SIZE; step += 1) {
            const candidate = getSeriesColor(start + step, mode);
            if (!used.has(candidate)) {
                used.add(candidate);
                return candidate;
            }
        }
        return preferred;
    });
};
